import * as RadixSelect from '@radix-ui/react-select';
import { useRef, useState, useSyncExternalStore, type KeyboardEvent, type MouseEvent } from 'react';

/**
 * The dashboard's dropdown. Every single-choice dropdown uses it, so they all
 * open the same menu in the dashboard's own style instead of the browser's.
 *
 * Built on Radix Select (the ARIA select-only combobox pattern: a combobox
 * trigger and a listbox of options, with arrows, Home/End, Enter/Space,
 * Escape, and type-ahead). Two additions on top:
 *
 * - Tab while the menu is open chooses the highlighted option and moves focus
 *   on, as the ARIA pattern says. Radix only blocks Tab there.
 * - Phones and tablets (no hover, coarse pointer) get the native <select>. The
 *   OS picker there is a full-size sheet or wheel, built for fingers, and it is
 *   what people expect on a phone. Its closed field looks the same as this one.
 *
 * The props mirror a controlled <select>: string values and an onChange that
 * gets the chosen value. An empty string is a normal value here, as it is for
 * a native option.
 */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'data-testid'?: string;
}

// Radix reserves '' for "nothing chosen", so '' travels under this name inside it.
const EMPTY = '\u0000empty';
const toRadix = (v: string) => (v === '' ? EMPTY : v);
const fromRadix = (v: string) => (v === EMPTY ? '' : v);

const TOUCH_QUERY = '(hover: none) and (pointer: coarse)';

function subscribeTouch(onChange: () => void) {
  const mq = window.matchMedia(TOUCH_QUERY);
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

/** True on phones and tablets, where the native picker is the better menu. */
export function useNativeSelect(): boolean {
  return useSyncExternalStore(
    subscribeTouch,
    () => window.matchMedia(TOUCH_QUERY).matches,
    () => false,
  );
}

export function Select(props: SelectProps) {
  return useNativeSelect() ? <NativeSelect {...props} /> : <MenuSelect {...props} />;
}

function NativeSelect({ value, onChange, options, disabled, ...rest }: SelectProps) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function MenuSelect({ value, onChange, options, disabled, ...rest }: SelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Set when Tab closes the menu: the direction focus moves on in.
  const tabbedRef = useRef<1 | -1 | 0>(0);
  // Set when a press starts on the trigger, so a click without one is a
  // click on the field's <label>, which only focuses, as it does for a native select.
  const pressedRef = useRef(false);

  // A value missing from the options shows the first option, as a native select does.
  const shown = options.find((o) => o.value === value) ?? options[0];

  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const item = (e.target as HTMLElement).closest<HTMLElement>('[role="option"]');
    const chosen = item && !item.hasAttribute('data-disabled') ? item.dataset.value : undefined;
    if (chosen !== undefined && chosen !== value) onChange(chosen);
    tabbedRef.current = e.shiftKey ? -1 : 1;
    setOpen(false);
  };

  const onCloseAutoFocus = (e: Event) => {
    const dir = tabbedRef.current;
    if (!dir || !triggerRef.current) return;
    tabbedRef.current = 0;
    e.preventDefault();
    focusNextTabbable(triggerRef.current, dir);
  };

  return (
    <RadixSelect.Root
      value={toRadix(value)}
      onValueChange={(v) => onChange(fromRadix(v))}
      open={open}
      onOpenChange={(next) => {
        pressedRef.current = false;
        setOpen(next);
      }}
      disabled={disabled}
    >
      <RadixSelect.Trigger
        ref={triggerRef}
        className="select-trigger"
        {...rest}
        onPointerDown={() => {
          pressedRef.current = true;
        }}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          const pressed = pressedRef.current;
          pressedRef.current = false;
          if (!pressed) {
            e.preventDefault();
            e.currentTarget.focus();
          }
        }}
      >
        <span className="select-value">
          <RadixSelect.Value>{shown?.label ?? ''}</RadixSelect.Value>
          {/* Every label, invisible, in the same cell: the field is as wide as
              its widest option, as a native select is, so choosing never
              shifts the layout. Drawn as generated content so it stays out of
              the field's text. */}
          <span className="select-sizer" aria-hidden="true">
            {options.map((o) => (
              <span key={o.value} data-label={o.label} />
            ))}
          </span>
        </span>
        <RadixSelect.Icon className="select-chevron">
          <Chevron />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className="select-menu"
          position="popper"
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onKeyDown={onMenuKeyDown}
          onCloseAutoFocus={onCloseAutoFocus}
        >
          <RadixSelect.ScrollUpButton className="select-scroll">
            <Chevron up />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className="select-viewport">
            {options.map((o) => (
              <RadixSelect.Item
                key={o.value}
                value={toRadix(o.value)}
                textValue={o.label}
                disabled={o.disabled}
                className="select-item"
                data-value={o.value}
              >
                <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="select-check">
                  <Check />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="select-scroll">
            <Chevron />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}

const TABBABLE =
  'a[href], button, input, select, textarea, iframe, summary, [tabindex], [contenteditable]:not([contenteditable="false"])';

/** Focuses the element after (or before) `from` in tab order. */
function focusNextTabbable(from: HTMLElement, dir: 1 | -1) {
  const all = Array.from(document.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) =>
      el === from ||
      (el.tabIndex >= 0 &&
        !(el as HTMLButtonElement).disabled &&
        !el.hasAttribute('data-radix-focus-guard') &&
        !el.closest('[inert], [hidden]') &&
        el.getClientRects().length > 0),
  );
  const next = all[all.indexOf(from) + dir];
  (next ?? from).focus();
}

function Chevron({ up = false }: { up?: boolean }) {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true" style={up ? { transform: 'rotate(180deg)' } : undefined}>
      <path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <path d="M2.75 7.25l2.75 2.75 5.75-6" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
